import React, { HTMLAttributes } from "react";
import { X } from "lucide-react";
import styles from "./MGTag.module.css";

export const MGTagAppearance = {
  error: "error",
  warning: "warning",
  success: "success",
  grey: "grey",
  green: "green",
  purple: "purple",
  blue: "blue",
  neutral: "neutral",
  "electric-blue": "electric-blue",
  "yellow-brand": "yellow-brand",
} as const;

export const MGTagSize = {
  xs: "xs",
  xxs: "xxs",
} as const;

export type TMGTagAppearance = keyof typeof MGTagAppearance;
export type TMGTagSize = keyof typeof MGTagSize;

export type TMGTagProps = Omit<HTMLAttributes<HTMLSpanElement>, "children"> & {
  appearance: TMGTagAppearance;
  closeAriaLabel?: string;
  dataTestId?: string;
  disabled?: boolean;
  label?: string;
  onClose?: (tagKey?: string) => void;
  size?: TMGTagSize;
  tagKey?: string;
};

const removableAppearances: ReadonlyArray<TMGTagAppearance> = [
  "grey",
  "green",
  "purple",
  "blue",
  "electric-blue",
  "yellow-brand",
];

export const MGTag = ({
  appearance,
  className,
  closeAriaLabel,
  dataTestId,
  disabled = false,
  label = "",
  onClose,
  size = "xs",
  tagKey,
  ...spanProps
}: TMGTagProps) => {
  const removable = !!onClose && removableAppearances.includes(appearance);
  const accessibleCloseLabel = closeAriaLabel || `Remover ${label}`;

  return (
    <span
      {...spanProps}
      className={`${styles.tag}${className ? ` ${className}` : ""}`}
      data-appearance={appearance}
      data-disabled={disabled || undefined}
      data-removable={removable || undefined}
      data-size={size}
      data-testid={dataTestId}
      aria-disabled={disabled || undefined}>
      <span className={styles.label}>{label}</span>
      {removable && (
        <button
          type="button"
          className={styles.close}
          disabled={disabled}
          aria-label={accessibleCloseLabel}
          title={accessibleCloseLabel}
          data-testid={dataTestId && `${dataTestId}-close`}
          onClick={() => onClose(tagKey)}>
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </span>
  );
};
